import type { ErrorResponse } from '@shared/types/http'

import { createURLParams } from '@scripts/shared/utils/create-url-params'
import { DataTableOrderEnum } from '@shared/features/data-table/type'
import { PostStatusEnum } from '@shared/features/posts/type'
import { endpoint } from '@shared/services/endpoint'
import { getRequest, handleResponse } from '@shared/services/fetcher'
import useSWR from 'swr'
import useSWRMutation from 'swr/mutation'

import type { ConfirmPaymentFormState } from '../ConfirmPaymentForm'
import type { BankAccount } from '../libs/type'

type CreateConfirmPaymentRequest = {
  slip: File
} & Omit<ConfirmPaymentFormState, 'slip'>

export function useConfirmPayment() {
  const useGetBankAccount = () => {
    return useSWR<BankAccount[], ErrorResponse>(
      `${endpoint.frontend.CONFIRM_PAYMENT.GET_BANK_ACCOUNTS}`,
      getRequest,
    )
  }

  const useCreate = () => {
    const {
      data,
      error,
      isMutating: isLoading,
      trigger,
    } = useSWRMutation<
      unknown,
      ErrorResponse,
      string,
      CreateConfirmPaymentRequest
    >(endpoint.frontend.CONFIRM_PAYMENT.INDEX, (url, { arg }) =>
      sendRequest(url, { arg }),
    )

    const create = async (request: CreateConfirmPaymentRequest) => {
      return await trigger(request)
    }

    return { create, data, error, isLoading }
  }

  return { create: useCreate, getBankAccount: useGetBankAccount }
}

export default useConfirmPayment

async function sendRequest<Request>(url: string, { arg }: { arg: Request }) {
  const response = await fetch(url, {
    body: JSON.stringify(arg),
    headers: {
      'Content-Type': 'application/json',
    },
    method: 'POST',
  })

  return handleResponse(response, 'POST')
}
